import { useEffect, useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { scrollToId } from "@/components/Shared";

const EASE = [0.22, 1, 0.36, 1];
const HERO_IMG = "/hero-mou.jpg";

const LINES = [
  { id: "hero-line-1", content: <>Architecting <em className="gold-text not-italic font-semibold">Sovereign</em></> },
  { id: "hero-line-2", content: <>Innovation &amp; High-Impact</> },
  { id: "hero-line-3", content: <><em className="gold-text not-italic font-semibold">Growth.</em></> },
];

export default function Hero() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 45, damping: 18 });
  const sy = useSpring(my, { stiffness: 45, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0.15]);
  const photoY = useTransform(scrollYProgress, [0, 1], [20, -70]);

  useEffect(() => {
    const onMove = (e) => {
      mx.set((e.clientX / window.innerWidth - 0.5) * 30);
      my.set((e.clientY / window.innerHeight - 0.5) * 20);
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [mx, my]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="grain relative flex min-h-screen flex-col justify-center overflow-hidden"
    >
      <div className="pointer-events-none absolute -left-40 bottom-0 -z-10 h-[380px] w-[380px] rounded-full bg-[radial-gradient(circle_at_center,rgba(38,64,120,0.5),transparent_70%)] blur-3xl" />

      <motion.div
        style={{ opacity: fade }}
        className="mx-auto grid w-full max-w-7xl items-center gap-12 px-5 pt-32 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:pt-20"
      >
        <div>
          <div className="overflow-hidden">
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
              className="h-px w-20 origin-left bg-[#D4AF37]"
            />
          </div>

          <h1
            data-testid="hero-headline"
            className="mt-7 font-serif text-4xl leading-[1.06] tracking-tight text-[#F7F5F0] sm:text-5xl lg:text-6xl xl:text-7xl"
          >
            {LINES.map((line, i) => (
              <span key={line.id} className="block overflow-hidden pb-1">
                <motion.span
                  data-testid={line.id}
                  className="block"
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.05, delay: 0.3 + i * 0.14, ease: EASE }}
                >
                  {line.content}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subcopy"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
            className="mt-7 max-w-xl text-base leading-relaxed text-[#94A3B8] sm:text-lg"
          >
            A private startup accelerator and strategic consulting house, pairing Government
            of India policy depth with private-sector velocity across deep-tech incubation,
            investor syndication, IP strategy and cross-border market entry.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-services"
              onClick={() => scrollToId("#services")}
              className="group flex items-center gap-3 bg-[#D4AF37] px-7 py-4 font-mono-alt text-[11px] uppercase tracking-[0.22em] text-[#070E1B] transition-colors duration-300 hover:bg-[#F3E5AB]"
            >
              Explore Core Services
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-contact"
              onClick={() => scrollToId("#contact")}
              className="border border-[#D4AF37]/50 px-7 py-4 font-mono-alt text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] transition-colors duration-300 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10"
            >
              Request Consultation
            </button>
          </motion.div>

          <motion.div
            data-testid="hero-credibility-badge"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.35 }}
            className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-[rgba(212,175,55,0.18)] pt-6 font-mono-alt text-[10px] uppercase tracking-[0.24em] text-[#64748B] sm:text-[11px]"
          >
            <span>Ex-MeitY Leadership</span>
            <span className="text-[#D4AF37]">◆</span>
            <span>6 MoU Partners</span>
            <span className="text-[#D4AF37]">◆</span>
            <span>MTech AI · IIT Jammu</span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, x: 48 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1.3, delay: 0.55, ease: EASE }}
          className="relative mx-auto w-full max-w-sm pb-8 lg:max-w-md"
        >
          <motion.div style={{ y: photoY }} className="relative">
            <motion.div style={{ x: sx, y: sy }} className="relative">
              <div className="pointer-events-none absolute -inset-10 -z-10 rounded-full animate-orb bg-[radial-gradient(circle_at_center,rgba(212,175,55,0.16),transparent_65%)] blur-3xl" />

              <div className="clip-frame relative z-10 border border-[rgba(212,175,55,0.4)] p-2.5">
                <div className="clip-frame overflow-hidden">
                  <motion.img
                    data-testid="hero-photo-main"
                    src={HERO_IMG}
                    alt="Bhumika Vyas holding the signed MoU with i-Hub Gujarat at Startup Conclave 2025"
                    className="aspect-[4/5] w-full object-cover"
                    whileHover={{ scale: 1.04 }}
                    transition={{ duration: 1.3, ease: EASE }}
                  />
                </div>
              </div>

              <motion.p
                data-testid="hero-photo-caption"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5, duration: 0.8 }}
                className="absolute -bottom-4 right-2 z-20 border border-[rgba(212,175,55,0.4)] bg-[#070E1B]/90 px-3 py-1.5 font-mono-alt text-[8px] uppercase tracking-[0.22em] text-[#E5C158] backdrop-blur-md sm:text-[9px]"
              >
                MoU Signing · i-Hub Gujarat · Sep 2025
              </motion.p>

              <div className="pointer-events-none absolute -right-4 -top-4 h-20 w-20 border-r border-t border-[#D4AF37]/50" />
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("#services")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 text-[#D4AF37]/70 transition-colors hover:text-[#D4AF37]"
        aria-label="Scroll to services"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2 }} className="block">
          <ArrowDown size={20} />
        </motion.span>
      </motion.button>
    </section>
  );
}
